import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { Phone, Mail, Globe, Linkedin, Download, UserPlus, Sparkles, Building2 } from "lucide-react";

export default function PublicCard() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const p = params.get("p");
    const q = p ? `?p=${encodeURIComponent(p)}` : "";
    fetch(`${API_BASE}/public/card/${id}${q}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setCard)
      .catch(() => setError("Carte introuvable"));
  }, [id, params]);

  const downloadVcf = () => {
    window.location.href = `${API_BASE}/public/card/${id}/vcard`;
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--bg)] px-5">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-xl bg-[var(--surface)] border border-[var(--border)] grid place-items-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-zinc-500" strokeWidth={1.8} />
          </div>
          <div className="font-display text-xl font-semibold text-[var(--text)]">Carte introuvable</div>
          <p className="text-sm text-zinc-500 mt-2">Ce lien n'est plus valide ou a été supprimé.</p>
        </div>
      </div>
    );
  }

  if (!card) {
    return <div className="min-h-screen grid place-items-center bg-[var(--bg)]"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;
  }

  const vcardUrl = `${API_BASE}/public/card/${id}/vcard`;
  const primary = card.brand?.primary || "#00DC82";

  return (
    <div className="min-h-screen bg-[var(--bg)] grid place-items-center py-8 px-4 relative">
      <div className="brand-glow absolute inset-0 pointer-events-none opacity-50" />
      <div className="w-full max-w-md relative">
        {/* Card */}
        <div className="rounded-2xl overflow-hidden bg-white border border-zinc-200 shadow-2xl shadow-black/40">
          <div className="p-6 text-white relative" style={{ background: `linear-gradient(135deg, ${primary} 0%, #D946EF 100%)` }}>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm overflow-hidden grid place-items-center ring-2 ring-white/40 shrink-0">
                {card.picture ? (
                  <img src={card.picture} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-3xl font-bold">{(card.name || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-2xl font-bold leading-tight" data-testid="public-card-name">{card.name}</div>
                {card.title && <div className="text-sm opacity-95 mt-0.5">{card.title}</div>}
                {card.company && <div className="text-xs opacity-85 flex items-center gap-1 mt-1"><Building2 className="w-3.5 h-3.5" />{card.company}</div>}
              </div>
            </div>
          </div>

          <div className="p-5 space-y-2.5 text-sm">
            {card.email && <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-zinc-700 py-2 border-b border-zinc-100 hover:text-zinc-900 transition-colors" data-testid="pc-email"><Mail className="w-4 h-4" style={{ color: primary }} /><span className="truncate">{card.email}</span></a>}
            {card.phone && <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-zinc-700 py-2 border-b border-zinc-100 hover:text-zinc-900 transition-colors" data-testid="pc-phone"><Phone className="w-4 h-4" style={{ color: primary }} />{card.phone}</a>}
            {card.website && <a href={card.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-700 py-2 border-b border-zinc-100 hover:text-zinc-900 transition-colors"><Globe className="w-4 h-4 shrink-0" style={{ color: primary }} /><span className="truncate">{card.website}</span></a>}
            {card.linkedin_url && <a href={card.linkedin_url.startsWith("http") ? card.linkedin_url : `https://${card.linkedin_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-700 py-2 hover:text-zinc-900 transition-colors"><Linkedin className="w-4 h-4 shrink-0" style={{ color: primary }} /><span className="truncate">{card.linkedin_url}</span></a>}
          </div>

          {/* QR + Add */}
          <div className="p-5 bg-zinc-50 border-t border-zinc-200">
            <Button
              onClick={downloadVcf}
              className="w-full rounded-full h-12 font-semibold text-white shadow-lg"
              style={{ backgroundColor: primary }}
              data-testid="add-to-contacts"
            >
              <UserPlus className="w-5 h-5 mr-2" />Ajouter à mes contacts
            </Button>
            <div className="mt-4 flex items-center justify-between gap-4">
              <div className="text-xs text-zinc-600 leading-relaxed">
                Ou scannez ce QR code avec l'appareil photo de votre téléphone pour ajouter les coordonnées instantanément.
              </div>
              <div className="bg-white rounded-xl p-2 border border-zinc-200 shrink-0" data-testid="public-qr">
                <QRCodeSVG value={vcardUrl} size={88} level="M" />
              </div>
            </div>
          </div>
        </div>

        {/* Prosperr branding */}
        <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-400 transition-colors" data-testid="powered-by">
          <Sparkles className="w-3.5 h-3.5" />
          Carte propulsée par {card.brand?.name || "Prosperr"}
        </a>
      </div>
    </div>
  );
}
